import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { formatNumber, formatDate, formatQty, parseQty, todayISO } from "@/lib/format";
import { Card, PageHeader, Button, Field, Input, Select, Modal, Textarea } from "@/components/ui-kit";
import BulkImport from "@/components/BulkImport";
import FilePreview from "@/components/FilePreview";
import MultiCheckFilter from "@/components/MultiCheckFilter";
import MasterCombo from "@/components/MasterCombo";
import { useTableFilter } from "@/hooks/useTableFilter";
import { usePagination } from "@/hooks/usePagination";
import { useAuth } from "@/contexts/AuthContext";
import { AutocompleteInput } from "@/components/AutocompleteInput";
import { recordHistoryMany } from "@/lib/history";
import { Plus, Trash2, X, Upload, FileText, Eye, Pencil, Check, Camera, Download, Info } from "lucide-react";
import { DatePicker } from "@/components/DatePicker";

const blank = {
  sj_number: "", date: todayISO(), client_name: "", po_id: "", category: "delivery",
  vehicle_no: "", driver: "", delivery_location: "", ekspeditur: "",
  items: [{ material_name: "", unit: "", quantity: 1, po_id: "", code: "", use_qty_billing: false, qty_billing: 0, use_manual_material: false }],
  notes: "", file_path: null, file_id: null, file_name: null, file_content_type: null,
  billing_count_allowed: 1,
};

export default function SuratJalan() {
  const { t } = useLang();
  const { hasPerm } = useAuth();
  const [rows, setRows] = useState([]);
  // Iter v34bh — Detail modal (task #5): tampilkan PO/No SJ/Klien/Tanggal/Qty/Status/Lampiran.
  const [detailRow, setDetailRow] = useState(null);
  const [pos, setPos] = useState([]);
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sjCodes, setSjCodes] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [edit, setEdit] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingAfter, setUploadingAfter] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  // Iter v34cq (#8) — Status plafon kredit klien terpilih (blokir SJ baru bila overdue > plafon).
  const [credit, setCredit] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("");        // (unused — kept for back-compat)
  const [codeFilter, setCodeFilter] = useState("");                 // (unused — kept for back-compat)
  const [billStatusFilter, setBillStatusFilter] = useState("");    // (unused — kept for back-compat)
  // Iter v34m (#3) — Unified multi-check filter (status + kategori + kode SJ)
  const [mfilter, setMfilter] = useState({ status: [], category: [], code: [], po: [] });
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [markMode, setMarkMode] = useState(false);
  const [markedIds, setMarkedIds] = useState([]);
  // Iter v34cq (#1) — Enrich rows dengan `po_number` (resolve dari po_id) supaya
  // kolom pencarian bisa menemukan SJ berdasarkan nomor PO yang tercantum.
  const rowsWithPo = React.useMemo(() => {
    const poMap = new Map(pos.map((p) => [p.id, p.po_number]));
    return rows.map((r) => ({ ...r, po_number: r.po_number || poMap.get(r.po_id) || "" }));
  }, [rows, pos]);
  const { filtered: textFiltered, FilterBar } = useTableFilter(rowsWithPo, ["sj_number", "client_name", "delivery_location", "vehicle_no", "date", "category", "items.material_name", "po_number"], "Cari SJ / No PO / klien / material / lokasi…");
  const _poFiltered = mfilter.po.length > 0
    ? textFiltered.filter((r) => {
        const hasPo = !!(r.po_id || (r.po_number || "").trim());
        return mfilter.po.includes(hasPo ? "with_po" : "no_po");
      })
    : textFiltered;
  const _baseFiltered = mfilter.category.length > 0
    ? _poFiltered.filter((r) => mfilter.category.includes(r.category || ""))
    : _poFiltered;
  const _codeFiltered = mfilter.code.length > 0
    ? _baseFiltered.filter((r) => {
        const sn = (r.sj_number || "").toUpperCase();
        return mfilter.code.some((c) => sn.startsWith((c || "").toUpperCase()));
      })
    : _baseFiltered;
  const _dateFiltered = _codeFiltered.filter((r) => {
    const d = r.date || "";
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  });
  // Iter v34b4 (fix #4) — partial dihilangkan; SJ berstatus partial diperlakukan sebagai "billed".
  const _statusOf = (r) => {
    const allowed = parseInt(r.billing_count_allowed || 1, 10);
    const count = parseInt(r.billed_count || 0, 10);
    const legacy = r.billed_invoice_id ? 1 : 0;
    const effective = Math.max(count, legacy);
    if (effective <= 0) return "unbilled";
    return "billed"; // termasuk yg dulu partial
  };
  const filtered = mfilter.status.length > 0
    ? _dateFiltered.filter((r) => mfilter.status.includes(_statusOf(r)))
    : _dateFiltered;
  // Iter v34{ac} — Standalone visible filter dropdown (Sudah/Belum Tertagih) +
  // total qty summary grouped by unit.
  const [billedFilter, setBilledFilter] = useState("all"); // all | billed | unbilled
  // Iter v34{ae} — Sort options: date/sj_number ascending or descending.
  const [sortBy, setSortBy] = useState("date_desc"); // date_desc | date_asc | sjno_asc | sjno_desc
  const filteredFinal = React.useMemo(() => {
    let base = filtered;
    if (billedFilter === "billed") base = filtered.filter((r) => _statusOf(r) === "billed" || _statusOf(r) === "partial");
    else if (billedFilter === "unbilled") base = filtered.filter((r) => _statusOf(r) === "unbilled");
    const arr = [...base];
    if (sortBy === "date_asc") arr.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    else if (sortBy === "date_desc") arr.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    else if (sortBy === "sjno_asc") arr.sort((a, b) => (a.sj_number || "").localeCompare(b.sj_number || "", undefined, { numeric: true }));
    else if (sortBy === "sjno_desc") arr.sort((a, b) => (b.sj_number || "").localeCompare(a.sj_number || "", undefined, { numeric: true }));
    return arr;
  }, [filtered, billedFilter, sortBy]);
  const totalQtyByUnit = React.useMemo(() => {
    const acc = {};
    for (const r of filteredFinal) {
      for (const it of (r.items || [])) {
        const u = (it.unit || "").trim() || "—";
        // Iter v34co (#5) — SUM ala Excel: round ke 2 desimal SEBELUM akumulasi
        // supaya konsisten dgn user manual sum (yang lihat display 2-desimal).
        const q = Math.round((Number(it.quantity || 0) || 0) * 100) / 100;
        acc[u] = (acc[u] || 0) + q;
      }
    }
    return acc;
  }, [filteredFinal]);
  const { pageItems, Pagination } = usePagination(filteredFinal, { perPage: 50 });

  const load = async () => {
    const [sR, pR, cR, catR, codeR, stR] = await Promise.all([
      api.get("/surat-jalan"), api.get("/pos"), api.get("/clients"),
      api.get("/masters/categories"), api.get("/masters/sj_codes"),
      api.get("/stocks"),
    ]);
    setRows(sR.data); setPos(pR.data); setClients(cR.data); setCategories(catR.data); setSjCodes(codeR.data); setStocks(stR.data);
  };
  useEffect(() => { load(); }, []);

  // Iter v34cq (#8) — Cek plafon kredit setiap kali klien pada form berubah.
  useEffect(() => {
    const name = (form.client_name || "").trim();
    if (!open || !name) { setCredit(null); return; }
    let alive = true;
    api.get("/credit-limits/check", { params: { client_name: name } })
      .then((r) => { if (alive) setCredit(r.data); })
      .catch(() => { if (alive) setCredit(null); });
    return () => { alive = false; };
  }, [open, form.client_name]);

  const creditBlocked = !!(credit?.blocked && !credit?.can_override);

  const openNew = () => { setEdit(null); setForm(blank); setOpen(true); };
  const openEdit = (sj) => {
    setEdit(sj);
    // Format numeric qty fields with Indonesian decimal (comma) for display.
    // parseQty handles both "10,5" and "10.5" on submit, so this is display-only.
    const items = (sj.items?.length ? sj.items : blank.items).map((i) => ({
      ...i,
      quantity: i.quantity !== undefined && i.quantity !== null ? formatQty(i.quantity) : "",
      qty_billing: i.qty_billing !== undefined && i.qty_billing !== null ? formatQty(i.qty_billing) : "",
    }));
    setForm({ ...blank, ...sj, items });
    setOpen(true);
  };

  const submit = async () => {
    try {
      const payload = {
        ...form,
        items: form.items.map((i) => ({
          ...i,
          quantity: parseQty(i.quantity),
          use_qty_billing: !!i.use_qty_billing,
          qty_billing: parseQty(i.qty_billing),
        })),
      };
      if (edit) {
        // Iter v34cw (#3) — Field photo_after_* dikelola oleh endpoint khusus
        // (/photo-after). Jangan ikut dikirim di PATCH agar photo_after_uploaded_at
        // tidak tertimpa null.
        const { photo_after_id, photo_after_name, photo_after_content_type, photo_after_uploaded_at, ...patchPayload } = payload;
        await api.patch(`/surat-jalan/${edit.id}`, patchPayload);
      } else {
        try {
          await api.post("/surat-jalan", payload);
        } catch (postErr) {
          // 409 = duplicate material/qty signature → ask user to confirm before forcing
          if (postErr?.response?.status === 409) {
            const detail = postErr?.response?.data?.detail || "Kemungkinan SJ duplikat.";
            if (!window.confirm(`${detail}\n\nTetap simpan?`)) {
              return;
            }
            await api.post("/surat-jalan?force=true", payload);
          } else {
            throw postErr;
          }
        }
      }
      // Persist autocomplete history for free-text fields (fire and forget)
      const histPairs = [
        { field: "sj.client_name", value: form.client_name },
        { field: "sj.vehicle_no", value: form.vehicle_no },
        { field: "sj.driver", value: form.driver },
        { field: "sj.delivery_location", value: form.delivery_location },
        { field: "sj.ekspeditur", value: form.ekspeditur },
      ];
      (form.items || []).forEach((it) => {
        if (it.use_manual_material) histPairs.push({ field: "sj.material_name", value: it.material_name });
      });
      recordHistoryMany(histPairs);
      toast.success("Surat Jalan tersimpan");
      setOpen(false); setEdit(null); setForm(blank); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    }
  };

  const del = async (id) => { if (!window.confirm("Hapus?")) return; await api.delete(`/surat-jalan/${id}`); load(); };
  const bulkDelete = async () => {
    if (!markedIds.length) return;
    if (!window.confirm(`Hapus ${markedIds.length} Surat Jalan? Stok akan otomatis dikembalikan.`)) return;
    try {
      await Promise.all(markedIds.map((id) => api.delete(`/surat-jalan/${id}`)));
      toast.success(`${markedIds.length} Surat Jalan dihapus`);
      setMarkedIds([]); setMarkMode(false); load();
    } catch (e) { toast.error("Gagal menghapus sebagian SJ"); }
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { material_name: "", unit: "", quantity: 1, po_id: form.po_id, code: "", use_qty_billing: false, qty_billing: 0 }] });
  const rmItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  const setItem = (idx, k, v) => { const items = [...form.items]; items[idx][k] = v; setForm({ ...form, items }); };

  const uploadSjFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("kind", "sj");
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, file_path: r.data.path, file_id: r.data.id, file_name: r.data.filename, file_content_type: r.data.content_type }));
      toast.success("Surat Jalan terunggah");
    } catch (err) { toast.error("Gagal upload"); }
    finally { setUploading(false); }
  };

  return (
    <div className="min-h-full bg-[#f6f5f0]">
    <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
      <PageHeader
        overline="Operasional · Pengiriman"
        title={t("sj_recap")}
        description="Catat & rekapitulasi Surat Jalan. Stok otomatis berkurang sesuai jumlah yang dikirim."
        action={<div className="flex items-center gap-2">{hasPerm("sj.edit") && (
          <Button variant="ghost" data-testid="sj-repair-photo-btn" onClick={async () => {
            if (!window.confirm("Pulihkan foto 'sesudah dikirim' yang hilang dari riwayat sistem?")) return;
            try {
              const r = await api.post("/surat-jalan/repair-photo-after");
              toast.success(r.data.restored_count ? `${r.data.restored_count} foto dipulihkan` : "Tidak ada foto yang perlu dipulihkan");
              load();
            } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memulihkan"); }
          }}>Pulihkan Foto</Button>
        )}{hasPerm("sj.create") && <BulkImport kind="sj" onDone={load} />}{hasPerm("sj.create") && <Button onClick={openNew} data-testid="add-sj-btn"><Plus className="h-4 w-4" />{t("input_sj")}</Button>}</div>}
      />

      {/* Bento KPI hero */}
      {(() => {
        // Iter v34b4 (fix #4) — Compute billed based on `_statusOf` (billed OR partial = sudah ditagih).
        // Remove partial card (merged into "Sudah Ditagih"). Total Qty = 30 hari terakhir (rolling window).
        const total = rows.length;
        const billed = rows.filter((r) => {
          const s = _statusOf(r);
          return s === "billed" || s === "partial";
        }).length;
        const unbilled = total - billed;
        // Rolling 30 days for Total Qty
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - 30);
        const cutoffStr = cutoff.toISOString().slice(0, 10);
        const rows30d = rows.filter((r) => (r.date || "") >= cutoffStr);
        // Iter v34co (#5) — SUM ala Excel: round ke 2 desimal per item sebelum akumulasi.
        const totalQty = rows30d.reduce((s, r) => s + (r.items || []).reduce((ss, it) => ss + (Math.round((Number(it.quantity) || 0) * 100) / 100), 0), 0);
        return (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-2xl bg-white border border-stone-200/70 p-4">
              <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Total SJ</div>
              <div className="font-display text-3xl mt-1 text-stone-900">{total}</div>
            </div>
            <div className="rounded-2xl bg-white border border-stone-200/70 p-4">
              <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Total Qty Kirim (30 hari)</div>
              <div className="font-mono-num text-xl mt-1 text-stone-900">{totalQty.toLocaleString("id-ID", { maximumFractionDigits: 2 })}</div>
            </div>
            <div className="rounded-2xl bg-emerald-50/60 border border-emerald-100 p-4">
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Sudah Ditagih</div>
              <div className="font-display text-3xl mt-1 text-emerald-800">{billed}</div>
            </div>
            <div className="rounded-2xl bg-blue-50/60 border border-blue-100 p-4">
              <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">Belum Ditagih</div>
              <div className="font-display text-3xl mt-1 text-blue-800">{unbilled}</div>
            </div>
          </div>
        );
      })()}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        {FilterBar}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            <DatePicker value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="!w-auto !text-xs" placeholder="Dari" data-testid="sj-date-from" title="Tanggal dari" />
            <span className="text-stone-400 text-xs">—</span>
            <DatePicker value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="!w-auto !text-xs" placeholder="Sampai" data-testid="sj-date-to" title="Tanggal sampai" />
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(""); setDateTo(""); }} className="text-stone-400 hover:text-stone-700 text-xs px-1" title="Reset tanggal" data-testid="sj-date-reset">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Select value={codeFilter} onChange={(e) => setCodeFilter(e.target.value)} className="!hidden" data-testid="sj-code-filter-legacy" />
          <Select value={billStatusFilter} onChange={(e) => setBillStatusFilter(e.target.value)} className="!hidden" data-testid="sj-status-filter-legacy" />
          <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="!hidden" data-testid="sj-category-filter-legacy" />
          <Select value={billedFilter} onChange={(e) => setBilledFilter(e.target.value)} className="!h-9 !text-xs" data-testid="sj-billed-filter">
            <option value="all">Semua Tagihan</option>
            <option value="billed">Sudah Tertagih</option>
            <option value="unbilled">Belum Tertagih</option>
          </Select>
          {/* Iter v34{ae} — Sort by SJ number / Date. */}
          <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="!h-9 !text-xs" data-testid="sj-sort">
            <option value="date_desc">Tanggal ↓ (terbaru)</option>
            <option value="date_asc">Tanggal ↑ (terlama)</option>
            <option value="sjno_asc">No SJ ↑ (A→Z)</option>
            <option value="sjno_desc">No SJ ↓ (Z→A)</option>
          </Select>
          <MultiCheckFilter
            testidPrefix="sj-filter"
            value={mfilter}
            onChange={setMfilter}
            groups={[
              {
                key: "status",
                label: "Status Penagihan",
                options: [
                  { value: "billed", label: "Sudah Ditagihkan" },
                  // Iter v34b4 (fix #4) — partial dihilangkan; masuk kategori "Sudah Ditagihkan"
                  { value: "unbilled", label: "Belum Ditagihkan" },
                ],
              },
              {
                key: "po",
                label: "PO Terkait",
                options: [
                  { value: "no_po", label: "Belum ada PO" },
                  { value: "with_po", label: "Sudah ada PO" },
                ],
              },
              {
                key: "code",
                label: "Kode SJ",
                options: sjCodes.map((c) => ({ value: c.name, label: c.name })),
              },
              {
                key: "category",
                label: "Kategori",
                options: categories.map((c) => ({ value: c.name, label: c.name })),
              },
            ]}
          />
          {hasPerm("sj.delete") && (
            <Button variant={markMode ? "primary" : "secondary"} className="!py-1.5 !px-3 text-xs" onClick={() => { setMarkMode(!markMode); setMarkedIds([]); }} data-testid="sj-mark-toggle">
              {markMode ? "Selesai Menandai" : "Tandai"}
            </Button>
          )}
          {markMode && markedIds.length > 0 && (
            <Button variant="secondary" className="!py-1.5 !px-3 text-xs !text-red-700 !border-red-300" onClick={bulkDelete} data-testid="sj-bulk-delete">
              <Trash2 className="h-3.5 w-3.5" />Hapus {markedIds.length}
            </Button>
          )}
        </div>
      </div>

      {/* Iter v34{ac} — Total Qty summary panel (grouped by unit). */}
      {Object.keys(totalQtyByUnit).length > 0 && (
        <Card className="px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" data-testid="sj-total-qty-panel">
          <span className="text-stone-600 font-medium">Total Qty ({filteredFinal.length} SJ):</span>
          {Object.entries(totalQtyByUnit).map(([unit, qty]) => (
            <span key={unit} className="font-mono-num text-stone-800" data-testid={`sj-total-qty-${unit}`}>
              <b>{Number.isInteger(qty) ? qty.toLocaleString("id-ID") : Number(qty.toFixed(2)).toLocaleString("id-ID")}</b> <span className="text-stone-500">{unit}</span>
            </span>
          ))}
        </Card>
      )}

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-stone-50 border-b border-stone-200 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-600">
              {markMode && (
                <th className="py-3 px-3 w-8">
                  <input type="checkbox"
                    checked={filteredFinal.length > 0 && filteredFinal.every((r) => markedIds.includes(r.id))}
                    onChange={(e) => { setMarkedIds(e.target.checked ? filteredFinal.map((r) => r.id) : []); }}
                    data-testid="sj-mark-all"
                    title="Tandai semua"
                  />
                </th>
              )}
              <th className="py-3 px-3">No SJ</th>
              <th className="py-3 px-3">Tanggal</th>
              <th className="py-3 px-3">{t("client_name")}</th>
              <th className="py-3 px-3">{t("delivery_location")}</th>
              <th className="py-3 px-3">{t("vehicle_no")}</th>
              <th className="py-3 px-3 text-right">Qty per Item</th>
              <th className="py-3 px-3 text-center">Status</th>
              <th className="py-3 px-3 text-right">{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filteredFinal.length === 0 && (
              <tr><td colSpan={markMode ? 9 : 8} className="py-12 text-center text-stone-400">Tidak ada Surat Jalan.</td></tr>
            )}
            {pageItems.map((r, idx) => {
              const status = _statusOf(r);
              return (
              <tr key={r.id} className={`border-b border-stone-100 hover:bg-stone-50/50 ${markedIds.includes(r.id) ? "bg-amber-50/40" : ""}`} data-testid={`sj-row-${idx}`}>
                {markMode && (
                  <td className="py-2.5 px-3">
                    <input type="checkbox"
                      checked={markedIds.includes(r.id)}
                      onChange={(e) => setMarkedIds((prev) => e.target.checked ? [...prev, r.id] : prev.filter((x) => x !== r.id))}
                      data-testid={`sj-mark-${idx}`}
                    />
                  </td>
                )}
                <td className="py-2.5 px-3 font-mono">{r.sj_number}</td>
                <td className="py-2.5 px-3 font-mono text-stone-600">{formatDate(r.date)}</td>
                <td className="py-2.5 px-3 font-medium">{r.client_name}</td>
                <td className="py-2.5 px-3 text-stone-600">{r.delivery_location || "—"}</td>
                <td className="py-2.5 px-3 font-mono text-stone-600">{r.vehicle_no || "—"}</td>
                <td className="py-2.5 px-3 text-right font-mono-num text-xs">
                  {(r.items && r.items.length > 0) ? (
                    <div className="space-y-0.5">
                      {r.items.slice(0, 3).map((it, i) => (
                        <div key={i} className="text-stone-700">
                          <span className="text-stone-400 font-normal">{(it.material_name || "—").slice(0, 22)}:</span> {formatQty(it.quantity || 0)} {it.unit || ""}
                          {/* Iter v34cq (#3) — tampilkan qty penagihan bila berbeda dari qty kirim. */}
                          {it.use_qty_billing && (
                            <span className="ml-1 text-amber-700" title="Qty penagihan berbeda" data-testid={`sj-qty-billing-${idx}-${i}`}>
                              (tagih: {formatQty(it.qty_billing || 0)})
                            </span>
                          )}
                        </div>
                      ))}
                      {r.items.length > 3 && <div className="text-[10px] text-stone-400">+{r.items.length - 3} item lain</div>}
                    </div>
                  ) : <span className="text-stone-300">—</span>}
                </td>
                <td className="py-2.5 px-3 text-center">
                  {status === "billed" ? (
                    /* Iter v34bh — DONE badge: bg hijau #53db1e, font HITAM (bukan putih). */
                    <span
                      title={`Selesai Ditagih (${r.billed_invoice_number || "—"})`}
                      className="inline-flex items-center gap-1 px-2 rounded-full border text-[10px] font-bold uppercase"
                      style={{ backgroundColor: "#53db1e", borderColor: "#43b016", color: "#000" }}
                      data-testid={`sj-status-billed-${idx}`}
                    >
                      <Check className="h-3 w-3" style={{ color: "#000" }} /> Done
                    </span>
                  ) : status === "partial" ? (
                    <span title={`Partial (${r.billed_count}/${r.billing_count_allowed})`} className="inline-flex items-center justify-center h-6 px-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-bold font-mono" data-testid={`sj-status-partial-${idx}`}>
                      {r.billed_count}/{r.billing_count_allowed}
                    </span>
                  ) : r.photo_after_id ? (
                    /* Iter v34bh — READY badge: background KUNING (bukan hijau). Font tetap hitam untuk keterbacaan. */
                    <span title="Foto sesudah kirim sudah upload — siap ditagih" className="inline-flex items-center gap-1 px-2 rounded-full bg-yellow-300 text-yellow-950 border border-yellow-500 text-[10px] font-bold uppercase" data-testid={`sj-status-ready-${idx}`}>
                      <Check className="h-3 w-3" /> Ready
                    </span>
                  ) : (
                    <span title="Belum Ditagih" className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-stone-100 text-stone-500 border border-stone-200" data-testid={`sj-status-unbilled-${idx}`}>
                      <X className="h-3.5 w-3.5" />
                    </span>
                  )}
                </td>
                <td className="py-2.5 px-3 text-right">
                  {/* Iter v34bh — Refactor Aksi: (a) Tombol Detail baru (info icon) buka modal berisi PO/No SJ/Klien/Tanggal/Qty/Status/Lampiran. (b) Eye foto before+after dipindah ke dalam Detail. (c) Upload+Camera digabung 1 icon (native mobile UI biarkan user pilih gallery/kamera). */}
                  <button onClick={() => setDetailRow(r)} className="text-stone-500 hover:text-blue-700 px-2" title="Lihat detail lengkap" data-testid={`sj-detail-${idx}`}>
                    <Info className="h-3.5 w-3.5" />
                  </button>
                  {/* Iter v34cw (#3) — Tombol unduh / upload / hapus "foto sesudah" DIHAPUS dari tabel.
                      Unggah & hapus lampiran lewat Edit SJ; unduh lewat "Lihat Detail Lengkap". */}
                  {hasPerm("sj.edit") && <button onClick={() => openEdit(r)} className="text-stone-500 hover:text-blue-700 px-2" title="Edit" data-testid={`sj-edit-${idx}`}><Pencil className="h-3.5 w-3.5" /></button>}
                  {hasPerm("sj.delete") && <button onClick={() => del(r.id)} className="text-stone-500 hover:text-red-700 px-2" title="Hapus" data-testid={`sj-delete-${idx}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
      {Pagination}

      <Modal open={open} onClose={() => setOpen(false)} title={edit ? "Edit Surat Jalan" : t("input_sj")} size="xl">
        {(() => {
          // Iter v34cf — Sequential form: Tanggal → Pelanggan → PO. Bypass saat edit.
          const isEditingSj = !!edit;
          const sjDateDone = !!form.date;
          const sjClientDone = sjDateDone && !!(form.client_name || "").trim();
          const sjPoDone = sjClientDone; // PO opsional — dianggap "done" setelah Klien terisi (Tanpa PO valid).
          const sjClientLocked = !isEditingSj && !sjDateDone;
          const sjPoLocked = !isEditingSj && !sjClientDone;
          const sjRestLocked = !isEditingSj && !sjPoDone;
          return (
        <div className="space-y-4">
          {!isEditingSj && (
            <div className="bg-terra-50 border border-terra-200 p-3 rounded-sm text-[11px] text-terra-800 flex flex-wrap items-center gap-2" data-testid="sj-setup-hint">
              <span className="font-semibold">Setup Awal:</span>
              <span className={`px-2 py-0.5 rounded-full border ${sjDateDone ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-white border-stone-300 text-stone-500"}`} data-testid="sj-step-date-status">1. Tanggal {sjDateDone ? "✓" : "•"}</span>
              <span className={`px-2 py-0.5 rounded-full border ${sjClientDone ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-white border-stone-300 text-stone-500"}`} data-testid="sj-step-client-status">2. Pelanggan {sjClientDone ? "✓" : "•"}</span>
              <span className={`px-2 py-0.5 rounded-full border ${sjPoDone ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-white border-stone-300 text-stone-500"}`} data-testid="sj-step-po-status">3. PO {sjPoDone ? "✓" : "•"}</span>
            </div>
          )}
          {/* Iter v34cq (#8) — Peringatan / blokir plafon kredit klien. */}
          {credit && credit.blocked && (
            <div className={`p-3 rounded-sm text-xs border ${creditBlocked ? "bg-red-50 border-red-300 text-red-800" : "bg-amber-50 border-amber-300 text-amber-900"}`} data-testid="sj-credit-warning">
              <p className="font-semibold">
                {creditBlocked ? "Diblokir: Klien melebihi plafon kredit" : "Peringatan: Klien melebihi plafon kredit (Anda punya hak override)"}
              </p>
              <p className="mt-1">
                Tagihan jatuh tempo <b className="font-mono-num">{formatNumber(credit.overdue_amount || 0)}</b> dari plafon{" "}
                <b className="font-mono-num">{formatNumber(credit.credit_limit || 0)}</b> — kelebihan{" "}
                <b className="font-mono-num">{formatNumber(credit.excess || 0)}</b>.
                {creditBlocked && " Surat Jalan baru tidak dapat dibuat sebelum ada pembayaran pada tagihan yang sudah jatuh tempo."}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Tanggal" required>              <DatePicker value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="form-sj-date" />
            </Field>
            <Field label={t("client_name")} required hint={sjClientLocked ? "Isi Tanggal dulu" : ""}>
              <MasterCombo source="clients" value={form.client_name} disabled={sjClientLocked} onChange={(v) => setForm({ ...form, client_name: v, po_id: "" })} data-testid="form-sj-client" />
            </Field>
            <Field label="PO Terkait" hint={sjPoLocked ? "Isi Pelanggan dulu" : "Filter otomatis by Pelanggan"}>
              <Select value={form.po_id} disabled={sjPoLocked} onChange={(e) => {
                const po = pos.find(p => p.id === e.target.value);
                // Iter v34cw (#1) — Kategori SJ ikut kategori PO saat PO dipilih.
                setForm({
                  ...form,
                  po_id: e.target.value,
                  client_name: po?.client_name || form.client_name,
                  category: po ? (po.category || form.category) : form.category,
                });
              }} data-testid="form-sj-po">
                <option value="">— Tanpa PO —</option>
                {pos.filter((p) => !p.is_closed && (!p.fully_billed || p.id === form.po_id) && (!form.client_name || (p.client_name || "").toLowerCase() === form.client_name.toLowerCase())).map((p) => <option key={p.id} value={p.id}>{p.po_number} · {p.client_name}{p.fully_billed ? " (sudah penuh)" : ""}</option>)}
              </Select>
            </Field>
          </div>

          <fieldset disabled={sjRestLocked} className={sjRestLocked ? "opacity-50 pointer-events-none space-y-4" : "space-y-4"} data-testid="sj-rest-fieldset">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="No Surat Jalan" required hint="Pilih Kode SJ — nomor selanjutnya otomatis terisi sesuai kategori (bisa diedit).">
              <div className="flex gap-2">
                <Select className="!w-32 shrink-0" value="" onChange={async (e) => {
                  const code = e.target.value;
                  if (!code) return;
                  try {
                    const r = await api.get(`/next-sj-number?code=${encodeURIComponent(code)}&category=${encodeURIComponent(form.category || "")}`);
                    setForm({ ...form, sj_number: code + (r.data.next || "") });
                  } catch {
                    setForm({ ...form, sj_number: code });
                  }
                }} data-testid="form-sj-code">
                  <option value="">Kode SJ</option>
                  {sjCodes.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </Select>
                <Input value={form.sj_number}
                  onChange={(e) => {
                    // Allow only digits and dot in the suffix portion (after picked code) — but be permissive on full string edits.
                    const v = e.target.value;
                    setForm({ ...form, sj_number: v });
                  }}
                  data-testid="form-sj-number" placeholder="Nomor / sisa kode" />
              </div>
            </Field>
            <Field label={t("delivery_location")}>
              <Input value={form.delivery_location} onChange={(e) => setForm({ ...form, delivery_location: e.target.value })} />
            </Field>
            <Field label={t("vehicle_no")}>
              <Input value={form.vehicle_no} onChange={(e) => setForm({ ...form, vehicle_no: e.target.value })} />
            </Field>
            <Field label="Ekspeditur" hint="Nama perusahaan ekspedisi / forwarder. Tampil di Tahap 2 Tagihan, tidak di Rekap PDF.">
              <Input value={form.ekspeditur || ""} onChange={(e) => setForm({ ...form, ekspeditur: e.target.value })} data-testid="form-sj-ekspeditur" placeholder="PT Logistik Jaya" />
            </Field>
            <Field label="Kategori" hint={form.po_id ? "Mengikuti kategori PO terpilih" : undefined}>
              {form.po_id ? (
                /* Iter v34cw (#1) — Kategori mengikuti PO & dikunci selama memakai PO. */
                <Input value={form.category || ""} readOnly disabled className="bg-stone-100" data-testid="form-sj-category-locked" />
              ) : (
                <MasterCombo source="categories" value={form.category} onChange={(v) => setForm({ ...form, category: v })} placeholder="delivery, service…" data-testid="form-sj-category" />
              )}
            </Field>
            {hasPerm("sj.recurring_billing") && (
              <Field label="Bisa Ditagih Berkali-kali" hint="Default 1 — naikkan agar SJ bisa ditagihkan ulang (mis. untuk DP/pelunasan).">
                <Select value={form.billing_count_allowed || 1} onChange={(e) => setForm({ ...form, billing_count_allowed: parseInt(e.target.value) })} data-testid="form-sj-billing-count">
                  {[1,2,3,4,5,6,7,8,9,10].map((n) => <option key={n} value={n}>{n}x penagihan</option>)}
                </Select>
              </Field>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-stone-600">Item Surat Jalan</label>
              <Button variant="secondary" onClick={addItem} className="!py-1 !px-2 text-xs"><Plus className="h-3 w-3" />{t("add")}</Button>
            </div>
            <p className="text-[10px] text-stone-500 mb-2">
              {form.po_id ? "Nama material diambil dari PO terpilih." : "Tanpa PO — nama material diambil dari Stok Barang. Kode Stok akan otomatis terisi dan stok berkurang saat SJ disimpan."}
            </p>
            <div className="border border-stone-200 divide-y divide-stone-100">
              <div className="grid grid-cols-12 gap-2 px-2 py-1.5 bg-stone-50 text-[10px] uppercase tracking-widest font-semibold text-stone-600">
                <div className="col-span-2">Kode Stok</div>
                <div className="col-span-5">{t("material_name")}</div>
                <div className="col-span-2">{t("unit")}</div>
                <div className="col-span-2 text-right">Qty</div>
                <div className="col-span-1"></div>
              </div>
              {form.items.map((it, i) => {
                // Determine the dropdown source
                let materialOptions;
                if (form.po_id) {
                  const po = pos.find((p) => p.id === form.po_id);
                  materialOptions = (po?.items || []).map((pi) => ({
                    label: pi.material_name, value: pi.material_name, unit: pi.unit, code: "",
                  }));
                } else {
                  materialOptions = stocks.map((s) => ({
                    label: `${s.material_name}${s.code ? ` [${s.code}]` : ""}`,
                    value: s.material_name, unit: s.unit, code: s.code || "",
                  }));
                }
                // Also support filter by code
                const stockCodeOptions = stocks.map((s) => ({ value: s.code, label: s.code, material_name: s.material_name, unit: s.unit })).filter((s) => s.value);
                const handleCodePick = (codeVal) => {
                  const st = stocks.find((s) => s.code === codeVal);
                  if (st) {
                    setItem(i, "code", codeVal);
                    setItem(i, "material_name", st.material_name);
                    setItem(i, "unit", st.unit);
                  } else {
                    setItem(i, "code", codeVal);
                  }
                };
                const handleMaterialPick = (matVal) => {
                  const opt = materialOptions.find((o) => o.value === matVal);
                  setItem(i, "material_name", matVal);
                  if (opt) {
                    if (opt.unit) setItem(i, "unit", opt.unit);
                    if (opt.code) setItem(i, "code", opt.code);
                  }
                };
                return (
                  <div key={i} className="border-b border-stone-100 last:border-b-0">
                    <div className="grid grid-cols-12 gap-2 p-2 items-center">
                      <Select className="col-span-2" value={it.code || ""} onChange={(e) => handleCodePick(e.target.value)} data-testid={`sj-item-code-${i}`} disabled={!!it.use_manual_material}>
                        <option value="">— Kode —</option>
                        {stockCodeOptions.map((s, j) => <option key={j} value={s.value}>{s.value}</option>)}
                      </Select>
                      {it.use_manual_material ? (
                        <div className="col-span-5">
                          <AutocompleteInput
                            field="sj.material_name"
                            value={it.material_name || ""}
                            onChange={(v) => setItem(i, "material_name", v)}
                            placeholder="Ketik nama material manual…"
                            dataTestid={`sj-item-name-${i}`}
                          />
                        </div>
                      ) : (
                        <Select className="col-span-5" value={it.material_name || ""} onChange={(e) => handleMaterialPick(e.target.value)} data-testid={`sj-item-name-${i}`}>
                          <option value="">— Pilih material —</option>
                          {materialOptions.map((o, j) => <option key={j} value={o.value}>{o.label}</option>)}
                          {it.material_name && !materialOptions.find((o) => o.value === it.material_name) && (
                            <option value={it.material_name}>{it.material_name} (manual)</option>
                          )}
                        </Select>
                      )}
                      <div className="col-span-2"><MasterCombo source="units" value={it.unit} onChange={(v) => setItem(i, "unit", v)} placeholder={t("unit")} /></div>
                      <Input className="col-span-2 font-mono-num" placeholder="Qty" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} data-testid={`sj-item-qty-${i}`} />
                      <button onClick={() => rmItem(i)} className="col-span-1 text-stone-400 hover:text-red-700"><X className="h-4 w-4 mx-auto" /></button>
                    </div>
                    {/* Manual material toggle (#8) — Iter v34bm: dibatasi ke role dengan perm sj.manual_input */}
                    {hasPerm("sj.manual_input") && (
                      <div className="px-2 pb-1">
                        <label className="inline-flex items-center gap-2 text-[11px] text-stone-600">
                          <input
                            type="checkbox"
                            checked={!!it.use_manual_material}
                            onChange={(e) => setItem(i, "use_manual_material", e.target.checked)}
                            data-testid={`sj-item-use-manual-material-${i}`}
                          />
                          Nama material manual
                          <span className="text-stone-400 text-[10px]">— ketik bebas + history dropdown.</span>
                        </label>
                      </div>
                    )}
                    {/* PO remaining qty hint (#3) */}
                    {form.po_id && (() => {
                      const po = pos.find((p) => p.id === form.po_id);
                      const pi = po?.items?.find((x) => x.material_name === it.material_name);
                      if (!pi) return null;
                      const delivered = (po.delivered || {})[it.material_name] || 0;
                      const remaining = parseFloat(pi.quantity || 0) - delivered;
                      return (
                        <div className="px-2 pb-2 text-[10px] text-stone-500">
                          Sisa kuota PO untuk <b>{it.material_name}</b>:{" "}
                          <span className={remaining > 0 ? "text-emerald-700 font-semibold" : "text-red-700 font-semibold"}>
                            {formatQty(remaining)} {pi.unit}
                          </span>
                          {" "}({formatQty(delivered)} terkirim dari {formatQty(pi.quantity)})
                        </div>
                      );
                    })()}
                    {/* Qty Penagihan toggle + field (#4) */}
                    <div className="px-2 pb-2 grid grid-cols-12 gap-2 items-center">
                      <label className="col-span-7 inline-flex items-center gap-2 text-xs text-stone-600">
                        <input type="checkbox"
                          checked={!!it.use_qty_billing}
                          onChange={(e) => setItem(i, "use_qty_billing", e.target.checked)}
                          data-testid={`sj-item-use-qty-billing-${i}`}
                        />
                        Qty Penagihan berbeda
                        <span className="text-stone-400 text-[10px]">— aktifkan fitur ini jika qty penagihan berbeda.</span>
                      </label>
                      <div className="col-span-2 text-right text-[10px] uppercase text-stone-500 font-semibold">Qty Penagihan</div>
                      <Input className="col-span-2 font-mono-num"
                        placeholder="0"
                        disabled={!it.use_qty_billing}
                        value={it.qty_billing || ""}
                        onChange={(e) => setItem(i, "qty_billing", e.target.value)}
                        data-testid={`sj-item-qty-billing-${i}`}
                      />
                      <div className="col-span-1"></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <Field label={t("notes")}>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>

          <Field label={t("upload_file") + " Surat Jalan (JPG/PNG/PDF)"}>
            <div className="flex items-center gap-3 flex-wrap">
              <label className="inline-flex items-center gap-2 px-3 py-2 border border-stone-300 cursor-pointer hover:bg-stone-50 text-sm">
                <Upload className="h-4 w-4" />
                <span>{uploading ? "Mengunggah…" : "Pilih file"}</span>
                <input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={uploadSjFile} className="hidden" data-testid="sj-file-input" />
              </label>
              {form.file_path && (
                <>
                  <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
                    <FileText className="h-3 w-3" /> {form.file_name || "Terunggah"}
                  </span>
                  {form.file_id && (
                    <button onClick={() => setPreviewId({ id: form.file_id, name: form.file_name, ct: form.file_content_type })} className="text-terra-600 hover:text-terra-700 inline-flex items-center gap-1 text-xs" type="button">
                      <Eye className="h-3.5 w-3.5" />Preview
                    </button>
                  )}
                </>
              )}
            </div>
          </Field>

          {/* Iter v34cw (#3) — Unggah / hapus FOTO SESUDAH KIRIM dipindah ke sini
              (sebelumnya hanya ada di tabel Rekap SJ yang sudah dihilangkan). */}
          {(hasPerm("sj.edit") || hasPerm("sj.create")) && (
            <Field label="Upload Surat Jalan Sesudah Dikirim (JPG/PNG/PDF)" hint="Wajib agar SJ berstatus Ready dan bisa ditagihkan">
              <div className="flex items-center gap-3 flex-wrap">
                <label className="inline-flex items-center gap-2 px-3 py-2 border border-stone-300 cursor-pointer hover:bg-stone-50 text-sm">
                  <Upload className="h-4 w-4" />
                  <span>{uploadingAfter ? "Mengunggah…" : (form.photo_after_id ? "Ganti file" : "Pilih file")}</span>
                  <input type="file" accept=".jpg,.jpeg,.png,.pdf" className="hidden" data-testid="sj-photo-after-input"
                    onChange={async (e) => {
                      const f = e.target.files?.[0]; if (!f) return;
                      setUploadingAfter(true);
                      try {
                        const fd = new FormData(); fd.append("file", f); fd.append("kind", "sj");
                        const up = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                        if (edit) {
                          await api.post(`/surat-jalan/${edit.id}/photo-after`, { file_id: up.data.id, file_name: up.data.filename, file_content_type: up.data.content_type || f.type });
                        }
                        setForm((p) => ({ ...p, photo_after_id: up.data.id, photo_after_name: up.data.filename, photo_after_content_type: up.data.content_type || f.type }));
                        toast.success(edit ? "Foto SESUDAH terpasang — SJ ini siap ditagih ✓" : "Foto SESUDAH siap — akan tersimpan saat klik Simpan");
                        if (edit) load();
                      } catch (err) { toast.error(err?.response?.data?.detail || "Gagal upload"); }
                      finally { setUploadingAfter(false); e.target.value = ""; }
                    }} />
                </label>
                {form.photo_after_id && (
                  <>
                    <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
                      <FileText className="h-3 w-3" /> {form.photo_after_name || "Terunggah"}
                    </span>
                    <button type="button" onClick={() => setPreviewId({ id: form.photo_after_id, name: form.photo_after_name, ct: form.photo_after_content_type || "image/jpeg" })} className="text-terra-600 hover:text-terra-700 inline-flex items-center gap-1 text-xs" data-testid="sj-photo-after-preview">
                      <Eye className="h-3.5 w-3.5" />Preview
                    </button>
                    <button type="button" data-testid="sj-photo-after-remove"
                      onClick={async () => {
                        if (!window.confirm("Hapus foto sesudah kirim?")) return;
                        try {
                          if (edit) await api.delete(`/surat-jalan/${edit.id}/photo-after`);
                          setForm((p) => ({ ...p, photo_after_id: "", photo_after_name: "", photo_after_content_type: "" }));
                          toast.success("Foto sesudah dihapus");
                          if (edit) load();
                        } catch (err) { toast.error(err?.response?.data?.detail || "Gagal hapus"); }
                      }}
                      className="text-stone-500 hover:text-red-700 inline-flex items-center gap-1 text-xs">
                      <Trash2 className="h-3.5 w-3.5" />Hapus
                    </button>
                  </>
                )}
              </div>
            </Field>
          )}


          <div className="flex justify-end gap-2 pt-4 border-t border-stone-200">
            <Button variant="ghost" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={submit} disabled={!edit && creditBlocked} data-testid="save-sj-btn">{t("save")}</Button>
          </div>
          </fieldset>
        </div>
          );
        })()}
      </Modal>
      <FilePreview open={!!previewId} onClose={() => setPreviewId(null)} fileId={previewId?.id} filename={previewId?.name} contentType={previewId?.ct} topmost={!!detailRow} />

      {/* Iter v34bh — Detail SJ Modal (task #5): PO/No SJ/Klien/Tanggal/Qty items/Status/Lampiran. */}
      <Modal open={!!detailRow} onClose={() => setDetailRow(null)} title={detailRow ? `Detail Surat Jalan · ${detailRow.sj_number}` : ""} size="lg">
        {detailRow && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-stone-50 border border-stone-200 p-3">
              <div><div className="text-[10px] uppercase tracking-widest text-stone-500">No. SJ</div><div className="font-mono font-medium">{detailRow.sj_number}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-stone-500">Tanggal</div><div className="font-mono">{formatDate(detailRow.date)}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-stone-500">Nama Klien</div><div className="font-medium">{detailRow.client_name || "—"}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-stone-500">No. PO / Kontrak</div><div className="font-mono">{
                /* Iter v34bj — Resolve po_id → po_number via master pos. */
                (() => {
                  const po = pos.find((p) => p.id === detailRow.po_id);
                  return po?.po_number || detailRow.po_number || "—";
                })()
              }</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-stone-500">Lokasi Pengiriman</div><div>{detailRow.delivery_location || "—"}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-stone-500">No. Kendaraan</div><div className="font-mono">{detailRow.vehicle_no || "—"}</div></div>
              <div className="md:col-span-2"><div className="text-[10px] uppercase tracking-widest text-stone-500">Status Tagihan</div>
                <div className="mt-1">
                  {_statusOf(detailRow) === "billed" ? (
                    <span className="inline-flex items-center gap-1 px-2 rounded-full border text-[10px] font-bold uppercase" style={{ backgroundColor: "#53db1e", borderColor: "#43b016", color: "#000" }}><Check className="h-3 w-3" style={{ color: "#000" }} />Done · {detailRow.billed_invoice_number || "—"}</span>
                  ) : _statusOf(detailRow) === "partial" ? (
                    <span className="inline-flex items-center gap-1 px-2 rounded-full bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-bold">Partial {detailRow.billed_count}/{detailRow.billing_count_allowed}</span>
                  ) : detailRow.photo_after_id ? (
                    <span className="inline-flex items-center gap-1 px-2 rounded-full bg-yellow-300 text-yellow-950 border border-yellow-500 text-[10px] font-bold uppercase"><Check className="h-3 w-3" />Ready</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 rounded-full bg-stone-100 text-stone-600 border border-stone-200 text-[10px] font-bold uppercase">Belum Ditagih</span>
                  )}
                </div>
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Item / Quantity</div>
              <table className="w-full text-xs border border-stone-200">
                <thead className="bg-stone-50 text-left text-[10px] uppercase tracking-widest font-semibold text-stone-600">
                  <tr><th className="py-2 px-2">Material</th><th className="py-2 px-2 text-right">Qty</th><th className="py-2 px-2 text-right">Qty Penagihan</th><th className="py-2 px-2">Unit</th></tr>
                </thead>
                <tbody>
                  {(detailRow.items && detailRow.items.length > 0) ? detailRow.items.map((it, i) => (
                    <tr key={i} className="border-t border-stone-100">
                      <td className="py-1.5 px-2">{it.material_name || "—"}</td>
                      <td className="py-1.5 px-2 text-right font-mono">{formatQty(it.quantity || 0)}</td>
                      <td className="py-1.5 px-2 text-right font-mono" data-testid={`sj-detail-qty-billing-${i}`}>
                        {it.use_qty_billing
                          ? <span className="text-amber-700 font-semibold">{formatQty(it.qty_billing || 0)}</span>
                          : <span className="text-stone-400">sama</span>}
                      </td>
                      <td className="py-1.5 px-2 text-stone-600">{it.unit || "—"}</td>
                    </tr>
                  )) : <tr><td colSpan={4} className="py-3 text-center text-stone-400">Tanpa item</td></tr>}
                </tbody>
              </table>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Lampiran Foto / File</div>
              <div className="flex flex-wrap gap-2">
                {detailRow.file_id && (
                  <button onClick={() => setPreviewId({ id: detailRow.file_id, name: detailRow.file_name, ct: detailRow.file_content_type })} className="inline-flex items-center gap-1 border border-stone-300 px-2 py-1 hover:bg-blue-50 hover:border-blue-300 text-xs" data-testid="sj-detail-photo-before">
                    <Eye className="h-3 w-3 text-blue-700"/> Foto Sebelum Kirim
                  </button>
                )}
                {/* Iter v34cw (#3) — Unduh langsung lampiran dari modal detail. */}
                {detailRow.file_id && (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${detailRow.file_id}?download=1`} className="inline-flex items-center gap-1 border border-stone-300 px-2 py-1 hover:bg-blue-50 hover:border-blue-300 text-xs" data-testid="sj-detail-download-before">
                    <Download className="h-3 w-3 text-blue-700"/> Unduh Foto Sebelum
                  </a>
                )}
                {detailRow.photo_after_id && (
                  <button onClick={() => setPreviewId({ id: detailRow.photo_after_id, name: detailRow.photo_after_name, ct: detailRow.photo_after_content_type || "image/jpeg" })} className="inline-flex items-center gap-1 border border-stone-300 px-2 py-1 hover:bg-emerald-50 hover:border-emerald-300 text-xs" data-testid="sj-detail-photo-after">
                    <Eye className="h-3 w-3 text-emerald-700"/> Foto Sesudah Kirim
                  </button>
                )}
                {detailRow.photo_after_id && (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${detailRow.photo_after_id}?download=1`} className="inline-flex items-center gap-1 border border-stone-300 px-2 py-1 hover:bg-emerald-50 hover:border-emerald-300 text-xs" data-testid="sj-detail-download-after">
                    <Download className="h-3 w-3 text-emerald-700"/> Unduh Foto Sesudah
                  </a>
                )}
                {!detailRow.file_id && !detailRow.photo_after_id && <span className="text-xs text-stone-400 italic">Belum ada lampiran</span>}
              </div>
            </div>

            <div className="flex justify-end pt-3 border-t border-stone-100">
              <Button variant="ghost" onClick={() => setDetailRow(null)}>Tutup</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
    </div>
  );
}
